import { Gvc } from "../../schema/types/gvc/entity";
import { Location } from "../../schema/types/location/entity";
import { homeLink, linksOf, request, resourceLink } from "../../services/cpln";
import { parseLink } from "../../utils/linkParser/linkParser";

export interface GvcLocationsResult {
  enabled: string[];
  disabled: string[];
}

/**
 * Fetches all locations for a GVC by processing both locationLinks and locationQuery.
 * @param gvcNameOrLink - Either a GVC name or a full GVC link
 * @param gvcData - Optional: already fetched GVC data to avoid re-fetching
 * @returns Object with enabled and disabled location arrays (sorted alphabetically)
 */
export async function fetchGvcLocations(
  gvcNameOrLink: string,
  gvcData?: Gvc,
): Promise<GvcLocationsResult> {
  try {
    // Get GVC data if not provided
    let gvc: Gvc = gvcData as Gvc;
    if (!gvc) {
      // Check if it's a link or just a name
      const isLink = gvcNameOrLink.startsWith("/");
      const url = isLink ? gvcNameOrLink : resourceLink("gvc", gvcNameOrLink);
      const { data } = await request({ url });
      gvc = data;
    }

    const staticPlacement = gvc.spec?.staticPlacement;
    const enabledMap = new Map<string, string>();
    const disabledMap = new Map<string, string>();

    // Helper to add location to correct map based on spec.enabled
    const addLocation = (name: string, isEnabled: boolean) => {
      if (isEnabled) {
        if (!enabledMap.has(name)) {
          enabledMap.set(name, name);
        }
        // Remove from disabled if it was there
        disabledMap.delete(name);
      } else {
        // Only add to disabled if not already in enabled
        if (!enabledMap.has(name) && !disabledMap.has(name)) {
          disabledMap.set(name, name);
        }
      }
    };

    // Process staticPlacement.locationLinks - need to fetch each to check spec.enabled
    if (staticPlacement?.locationLinks && staticPlacement.locationLinks.length > 0) {
      const locationFetches = staticPlacement.locationLinks.map(async (link) => {
        try {
          const { data: loc } = await request<Location>({ url: link });
          return loc;
        } catch (e) {
          // If fetch fails, parse the name and assume enabled
          const parsed = parseLink(link, { useInputCtx: true });
          return { name: parsed.name || link, spec: { enabled: true } } as Location;
        }
      });

      const locations = await Promise.all(locationFetches);
      for (const loc of locations) {
        addLocation(loc.name, loc.spec?.enabled !== false);
      }
    }

    // Process staticPlacement.locationQuery - evaluate and follow next links
    if (staticPlacement?.locationQuery) {
      try {
        const queryBody = {
          kind: "location",
          spec: staticPlacement.locationQuery.spec,
        };

        let nextUrl: string | null = `${homeLink("location")}/-query`;
        let isFirstRequest = true;

        while (nextUrl) {
          const { data: queryResult } = await request({
            method: isFirstRequest ? "post" : "get",
            url: nextUrl,
            body: isFirstRequest ? queryBody : undefined,
          });

          const locations: Location[] = queryResult.items || [];
          for (const loc of locations) {
            addLocation(loc.name, loc.spec?.enabled !== false);
          }

          // Check for next page
          const nextLink = queryResult.links?.find((l: any) => l.rel === "next");
          nextUrl = nextLink?.href || null;
          isFirstRequest = false;
        }
      } catch (e) {
        // Query evaluation failed, continue with what we have
      }
    }

    return {
      enabled: Array.from(enabledMap.values()).sort((a, b) => a.localeCompare(b)),
      disabled: Array.from(disabledMap.values()).sort((a, b) => a.localeCompare(b)),
    };
  } catch (e) {
    return { enabled: [], disabled: [] };
  }
}

export interface GvcTableItem extends Gvc {
  selfLink: string;
}

interface GetGvcTableItemProps {
  link?: string;
  name?: string;
  item?: Gvc;
}
export function getGvcTableItem({ link, name, item }: GetGvcTableItemProps): GvcTableItem {
  if (item) {
    const selfLink = linksOf(item).self!;

    return {
      ...item,
      selfLink: selfLink,
    };
  }

  if (link) {
    const { name } = parseLink(link, { useInputCtx: true });

    return {
      kind: "gvc",
      name: name,
      selfLink: link,
      links: [{ rel: "self", href: link }],
      created: "",
      description: "",
      id: "",
      spec: {},
      lastModified: "",
      tags: {},
      version: 1,
    };
  }

  // assume name
  return {
    kind: "gvc",
    name: name!,
    selfLink: `TODO test if this breaks`,
    links: [],
    created: "",
    description: "",
    id: "",
    spec: {},
    lastModified: "",
    tags: {},
    version: 1,
  };
}
